import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

// Define un componente personalizado para los enlaces (<a>)
// Esto asegura que todos los enlaces renderizados por ReactMarkdown
// tengan el atributo target="_top" para romper el iframe.
const CustomLink = ({ href, children }) => {
  // Asegúrate de que el enlace no sea interno al contenido actual del iframe
  // y agrega el target="_top" para que se abra en la ventana padre.
  const target = href && href.startsWith('http') ? '_top' : '_self';
  return (
    <a href={href} target={target}>
      {children}
    </a>
  );
};

const graphs = [
  { 
    id: "graph1", 
    title: "Análisis general", 
    menuTitle: "General", 
    text: `En Chile, un 16% de las niñas y niños realiza trabajo infantil. Esta proporción equivale a 507.800 niñas y niños. Al desagregar por tipo de 
          trabajo infantil, se observa que un 5% realiza trabajos en la ocupación y un 11% trabajo doméstico.`, 
    link: "https://public.flourish.studio/visualisation/24356337/embed"
  },
  { 
    id: "graph2", 
    title: "Análisis por sexo", 
    menuTitle: "\u2022 Sexo", 
    text: `En el trabajo infantil total, no hay diferencias entre niñas y niños. En ambos grupos, un 16% realiza este tipo de actividades. Sin embargo, 
          si se observa por tipo de trabajo infantil si se encuentran diferencias entre géneros. Los niños tienden a trabajar en la ocupación en mayor 
          proporción que las niñas (7% frente a 4%) y las niñas, por su parte, realizan trabajo doméstico en mayor medida que los niños, con un 12% y 
          10%, respectivamente.`, 
    link: "https://public.flourish.studio/visualisation/24356312/embed"
  },
  { 
    id: "graph3", 
    title: "Análisis por edad", 
    menuTitle: "\u2022 Edad", 
    text: `A mayor edad, mayor proporción de niñas y niños realizan trabajo infantil. Entre las y los adolescentes, un 21% trabaja, frente a un 13% de 
          la infancia media y un 5% de la primera infancia. Esta tendencia se replica en ambos tipos de trabajo infantil.`, 
    link: "https://public.flourish.studio/visualisation/24358254/embed"
  },
  { 
    id: "graph4", 
    title: "Análisis por país de nacimiento", 
    menuTitle: "\u2022 País de nacimiento", 
    text: `No hay diferencias estadísticamente significativas entre niñas y niños que nacieron en el país y aquellos que no lo hicieron en la 
          proporción que realiza trabajo infantil.  Sin embargo, en el trabajo doméstico si se observan diferencias significativas: aquellas niñas y 
          niños que nacieron en otro país tienden a realizar en mayor medida trabajo doméstico. Entre ambos grupos hay 5 puntos de diferencias. Las 
          niñas y niños nacidos en otros países, como se observa en el indicador [Apoyo en el cuidado](https://www.observatorioninez.org/temasejes/apoyo-en-el-cuidado), tienden a tener menos redes de apoyo para el 
          cuidado. Si bien no es posible establecer esta asociación a partir de ambos indicadores, probablemente al tener menos redes, las familias 
          migrantes tengan que recurrir en mayor medida a sus hijos para satisfacer la demanda de cuidado al interior de su hogar.`, 
    link: "https://public.flourish.studio/visualisation/24358368/embed"
  },
  { 
    id: "graph5", 
    title: "Análisis por pueblo originario", 
    menuTitle: "\u2022 Pueblo originario", 
    text: `Las niñas y niños que se identifican con pueblos originarios tienden a realizar trabajo infantil en mayor proporción que aquellos que no 
          se identifican. Al estimar la proporción de trabajo infantil según la identificación con un pueblo originario para las niñas y niños que 
          residen en zonas urbanas y aquellos de zonas rurales, se obtiene que esta diferencia existe únicamente en zonas rurales, donde la proporción 
          de niñas y niños indígenas que trabaja alcanza un 30%, frente a un 19% de las y los que no se identifican.`, 
    link: "https://public.flourish.studio/visualisation/24358453/embed"
  },
  { 
    id: "graph6", 
    title: "Análisis por pobreza", 
    menuTitle: "\u2022 Pobreza", 
    text: `Las diferencias que se observan entre niñas y niños según si se encuentran en situación de pobreza no son estadísticamente significativas.`, 
    link: "https://public.flourish.studio/visualisation/24358476/embed"
  },
  { 
    id: "graph7", 
    title: "Análisis por quintil", 
    menuTitle: "\u2022 Quintil", 
    text: `En el trabajo infantil total, las diferencias son significativas únicamente entre el primer (18%) y el último quintil por ingreso (12%). 
          En el trabajo por ocupación ocurre lo mismo. Es interesante notar, sin embargo, que la proporción de niñas y niños que realiza trabajo 
          doméstico es igual en todos los quintiles, dando cuenta de que el trabajo doméstico es independiente del ingreso del hogar.`, 
    link: "https://public.flourish.studio/visualisation/24358524/embed"
  },
  { 
    id: "graph8", 
    title: "Análisis por zona de residencia", 
    menuTitle: "\u2022 Zona", 
    text: `Según la zona de residencia en que viven niñas y niños, hay diferencias estadísticamente significativas. Aquellas y aquellos que viven en 
          zonas rurales tienden a realizar trabajo infantil en mayor medida que sus pares de zonas urbanas. Esta brecha se da en el trabajo infantil 
          total (22% frente a 15%) y en trabajos por ocupación, donde entre las niñas y niños de zonas rurales alcanzan un 13%, más del doble que entre 
          las niñas y niños de zonas urbanas (5%).`, 
    link: "https://public.flourish.studio/visualisation/24358438/embed"
  },
  { 
    id: "graph9", 
    title: "Análisis por región", 
    menuTitle: "\u2022 Región", 
    text: `La proporción de niñas y niños que realizan trabajo infantil varía bastante según región. En La Araucanía, un 26% de las niñas y niños 
          trabaja y en Los Ríos un 20%. En el otro extremo, un 6% de las niñas y niños de Antofagasta trabaja, siendo esta la región con una menor 
          proporción de trabajo infantil.`, 
    link: "https://public.flourish.studio/visualisation/24358596/embed"
  },
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}> 
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  // Se pasa el componente personalizado para los enlaces
                  components={{ a: CustomLink }}
                >
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;

